import { Component, OnInit } from '@angular/core';
import * as moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService } from 'primeng/api';
import { AppService } from 'src/app/core/services/app.service';
import { FocuslinkService } from 'src/app/core/services/focuslink.service';
import { LocalDataStoreService } from 'src/app/core/services/local-data-store.service';
import { UserService } from 'src/app/core/services/user.service';
import { Dealer } from 'src/app/shared/models/Dealer.model';
import { UserRoleEnum } from 'src/app/shared/models/enums/user-role.enum';
import { User, UserVenue } from 'src/app/shared/models/user.model';
import { v4 as uuidv4 } from 'uuid';

@Component({
	selector: 'app-user-setup',
	templateUrl: './user-setup.component.html',
	styleUrls: ['../../commandcenter.scss'],
})
export class UserSetupComponent implements OnInit {
	dealers: Dealer[];

	moment: any = moment;

	userList: User[];
	userNew: boolean;
	userRoles = [];
	userStatus = [
		{
			label: 'Active',
			value: 1,
		},
		{
			label: 'Frozen',
			value: 2,
		},
		{
			label: 'Deleted',
			value: 3,
		},
		{
			label: 'Super Admin',
			value: 4,
		},
		{
			label: 'Pending Activation',
			value: 5,
		},
		{
			label: 'Password Reset Requested',
			value: 6,
		},
	];

	userSelected: User;
	userSelectedVenues: number[];
	userSelectedVenueCount: number;

	venueSelectionsAvailable: any[] = [];
	venues: {
		name: string;
		key: number;
		dealerKey: string;
	}[];

	constructor(
		public app: AppService,
		private focuslink: FocuslinkService,
		private localStorage: LocalDataStoreService,
		private spinner: NgxSpinnerService,
		private userService: UserService,
		public confirmationService: ConfirmationService
	) {}

	ngOnInit(): void {
		this.userListGet();
		this.init();
	}

	async init() {
		this.assignRolesFromCurrentUser();
		await this.assignCurrentUserVenues();
		if (this.userService.userSubscription.value.role === UserRoleEnum.SUPERADMIN || this.userService.userSubscription.value.role === UserRoleEnum.GOD) {
			this.dealersGet();
		} else {
			this.venueSelectionsAvailable = this.userService.userVenues.value
				.sort((a, b) => {
					const nameA = a.venueName.toLowerCase();
					const nameB = b.venueName.toLowerCase();
					return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
				})
				.map((i) => {
					return {
						label: i.venueName,
						value: i.venueKey,
					};
				});
		}
	}

	async assignRolesFromCurrentUser() {
		if (this.userService.userSubscription.value.role > UserRoleEnum.VENUEUSER) {
			this.userRoles.push({
				label: 'Venue User',
				value: UserRoleEnum.VENUEUSER,
			});
		}
		if (this.userService.userSubscription.value.role >= UserRoleEnum.VENUEADMIN) {
			this.userRoles.push({
				label: 'Venue Admin',
				value: UserRoleEnum.VENUEADMIN,
			});
		}
		if (this.userService.userSubscription.value.role >= UserRoleEnum.DEALERUSER) {
			this.userRoles.push({
				label: 'Dealer User',
				value: UserRoleEnum.DEALERUSER,
			});
		}
		if (this.userService.userSubscription.value.role >= UserRoleEnum.DEALERADMIN) {
			this.userRoles.push({
				label: 'Dealer Admin',
				value: UserRoleEnum.DEALERADMIN,
			});
		}
		if (this.userService.userSubscription.value.role >= UserRoleEnum.SUPERADMIN) {
			this.userRoles.push({
				label: 'Super Admin',
				value: UserRoleEnum.SUPERADMIN,
			});
		}
		if (this.userService.userSubscription.value.role === UserRoleEnum.GOD) {
			this.userRoles.push({
				label: 'Holy One',
				value: UserRoleEnum.GOD,
			});
		}
	}

	async assignCurrentUserVenues() {
		this.venues = this.userService.userVenues.value.map((i) => {
			return {
				name: i.venueName,
				key: i.venueKey,
				dealerKey: i.dealerKey,
			};
		});
	}

	async dealersGet() {
		const resp = await this.focuslink.dealersGet(false);
		this.dealers = resp.map((i) => new Dealer(i));
		this.organizeVenueList();
	}

	organizeVenueList() {
		console.log('organizeVenueList');
		for (let dealer of this.dealers) {
			this.venueSelectionsAvailable.push({
				label: dealer.name,
				value: dealer.key,
				items: this.venues
					.filter((i) => i.dealerKey === dealer.key)
					.map((i) => {
						return {
							label: i.name,
							dealerKey: i.dealerKey,
							value: i.key,
						};
					}),
			});
		}
	}

	async userListGet() {
		const resp = await this.focuslink.userGetAll(this.userService.userSubscription.value.key);
		this.userList = resp.map((i) => {
			const u = new User(i);
			u.tempFullName = `${u.firstName} ${u.lastName}`;
			return u;
		});
		this.userList.sort((a, b) => (a.organization > b.organization ? 1 : -1));
	}

	async userAdd() {
		this.spinner.show();
		this.userNew = true;
		this.userSelectedVenueCount = 0;
		this.userSelectedVenues = [];
		this.userSelected = new User({
			key: uuidv4(),
			role: UserRoleEnum.VENUEUSER,
			dealerKey: this.userService.userSubscription.value.dealerKey || undefined,
			created: new Date(),
			invitedByUserKey: this.userService.userSubscription.value.key,
			unsaved: true,
		});
		this.spinner.hide();
	}

	async userDelete(userKey: string) {
		this.userList.splice(
			this.userList.findIndex((i) => i.key === userKey),
			1
		);
	}

	async userDealerSelect() {
		if (this.userSelected.role === 3 || this.userSelected.role === 4) {
			this.userSelected.organization = this.dealers.find((i) => i.key === this.userSelected.dealerKey).name;
		} else if (this.userSelected.role === 5) {
			this.userSelected.organization = 'Focus POS Systems';
		}
	}

	async userEdit() {
		this.spinner.show();
		if (this.userSelected.UserVenue && this.userSelected.UserVenue.length > 0) {
			this.userSelectedVenues = this.userSelected.UserVenue.map((i) => i.venueKey);
			this.userSelectedVenueCount = this.userSelectedVenues.length;
		}
		this.spinner.hide();
	}

	async userPermissionRemove(permissionKey) {
		const idx = this.userSelected.UserPermission.findIndex((i) => i.permissionKey === permissionKey);
		this.userSelected.UserPermission.splice(idx, 1);
	}

	async userSave() {
		if (this.userSelected.role === UserRoleEnum.VENUEUSER || this.userSelected.role === UserRoleEnum.VENUEADMIN) {
			for (let venue of this.userSelectedVenues) {
				let existingVenue;
				if (this.userSelected.UserVenue && this.userSelected.UserVenue.length > 0) {
					existingVenue = this.userSelected.UserVenue.find((i) => i.venueKey === venue);
				} else {
					this.userSelected.UserVenue = [];
				}

				if (!existingVenue) {
					this.userSelected.UserVenue.push(new UserVenue({ userKey: this.userSelected.key, venueKey: venue }));
				}
			}
			for (let venue of this.userSelected?.UserVenue) {
				const vk = this.userSelectedVenues.find((i) => i === venue.venueKey);
				if (!vk) {
					const idx = this.userSelected.UserVenue.findIndex((i) => i.venueKey === venue.venueKey);
					if (idx !== -1) {
						this.userSelected.UserVenue.splice(idx, 1);
					}
				}
			}
		}
		const userInFlight = { ...this.userSelected };
		if (this.userNew) {
			await this.focuslink.userInvite(this.userSelected);
			this.userSelected = undefined;
			await this.userListGet();
		} else {
			await this.focuslink.userEdit(this.userSelected);
			const idx = this.userList.findIndex((i) => i.key === userInFlight.key);
			this.userList[idx] = userInFlight;
			this.userSelected = undefined;
		}
	}

	async userVenueChange(event) {
		if (this.userSelectedVenueCount > event.value.length) {
			//removed venue
			let idx = this.userSelected.UserVenue.findIndex((i) => i.venueKey === event.itemValue);
			this.userSelected.UserVenue.splice(idx, 1);
			idx = this.userSelectedVenues.findIndex((i) => i === event.itemValue);
			this.userSelectedVenues.splice(idx, 1);
			this.userSelectedVenueCount -= 1;
			return;
		}
		if (this.userSelectedVenueCount < event.value.length) {
			//added venue
			this.userSelected.UserVenue.push(
				new UserVenue({
					userKey: this.userSelected.key,
					venueKey: event.itemValue,
					created: new Date(),
				})
			);
			this.userSelectedVenues.push(event.itemValue);
			this.userSelectedVenueCount += 1;
			return;
		}
	}
}
